import React, { useState, useEffect } from 'react';
import BenefitsSection from './BenefitsSection';
import TitleAndSubTitleBlock from './TitleAndSubTitleBlock';
import TilesGridSection from './TilesGridSection';
import { SCMBenefitsMainProps } from './Types';
import { themeColor } from '../../../services/styleUtils/colorUtils';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@mui/material';
import {
  headingColor,
  normalizeHeadingLevel,
} from '../../../services/styleUtils/headingStyleUtils';
import { getContentStyle } from '../../../services/styleUtils/contentStyleUtils';
import { getTextColorClass } from '../../../services/generalUtils';

const TrainingBenefitsMain = ({ fields }: SCMBenefitsMainProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:767px)');

  const [titleStyle, setTitleStyle] = useState({
    level: fields?.titleStyle?.fields?.level || 'h2',
    color: getTextColorClass(
      fields.titleStyle?.fields?.color
        ? headingColor(theme, fields.titleStyle?.fields?.color)
        : '#FFF',
    ),
  });
  const [subTitleStyle, setSubTitleStyle] = useState(
    getContentStyle(theme, fields?.subTitleStyle?.fields || {}),
  );
  const [tileFontStyle, setTileFontStyle] = useState(
    getContentStyle(theme, fields?.tileFontStyle?.fields || {}),
  );

  const [benefits, setBenefits] = useState(
    fields.benefitList?.map((item) => item.fields.textValue) || [],
  );

  const [useGradient, setUseGradient] = useState(
    fields.useGradient?.toLowerCase() === 'true',
  );

  const [sectionBackgroundImageUrl, setSectionBackgroundImageUrl] = useState(
    fields.backgroundImage?.url || '',
  );

  const [tileBackgroundImageUrl, setTileBackgroundImageUrl] = useState(
    fields.tileBackgroundImage?.url || '',
  );

  const [tileBackgroundColor, setTileBackgroundColor] = useState(
    themeColor(theme, fields?.tileBackgroundColor?.fields?.color),
  );

  useEffect(() => {
    const updatedLevel = fields?.titleStyle?.fields?.level || 'h2';
    const updatedColor = getTextColorClass(
      fields.titleStyle?.fields?.color
        ? headingColor(theme, fields.titleStyle?.fields?.color)
        : '#FFF',
    );

    setTitleStyle({
      level: updatedLevel,
      color: updatedColor,
    });

    setSubTitleStyle(
      getContentStyle(theme, fields?.subTitleStyle?.fields || {}),
    );
  }, [fields.titleStyle, theme]);

  useEffect(() => {
    setBenefits(fields.benefitList?.map((item) => item.fields.textValue) || []);
  }, [fields.benefitList]);

  useEffect(() => {
    setUseGradient(fields.useGradient?.toLowerCase() === 'true');
  }, [fields.useGradient]);

  useEffect(() => {
    setSectionBackgroundImageUrl(fields.backgroundImage?.url || '');
  }, [fields.backgroundImage]);

  useEffect(() => {
    setTileBackgroundImageUrl(fields.tileBackgroundImage?.url || '');
  }, [fields.tileBackgroundImage]);

  useEffect(() => {
    setTileBackgroundColor(
      themeColor(theme, fields?.tileBackgroundColor?.fields?.color),
    );
  }, [fields.tileBackgroundColor, theme]);

  useEffect(() => {
    setSubTitleStyle(
      getContentStyle(theme, fields?.subTitleStyle?.fields || {}),
    );
  }, [fields.subTitleStyle, theme]);

  useEffect(() => {
    setTileFontStyle(
      getContentStyle(theme, fields?.tileFontStyle?.fields || {}),
    );
  }, [fields.tileFontStyle, theme]);

  const titleBlockBgImage = isSmallScreen
    ? sectionBackgroundImageUrl
    : undefined;

  return (
    <BenefitsSection
      backgroundImage={!isSmallScreen ? sectionBackgroundImageUrl : undefined}
      gradientOverlay={useGradient}
    >
      <div className="flex flex-col md:flex-row w-full">
        <div className="w-full md:w-1/2 flex items-center justify-center">
          <TitleAndSubTitleBlock
            title={fields.title}
            subtitle={fields.subTitle}
            backgroundImage={titleBlockBgImage}
            gradientOverlay={useGradient}
            subTitleStyle={subTitleStyle}
            titleLevel={normalizeHeadingLevel(titleStyle.level)}
            titleTextColor={titleStyle.color}
          />
        </div>
        <div className="w-full md:w-1/2 flex items-center justify-center pb-15">
          <TilesGridSection
            tiles={benefits}
            backgroundImage={tileBackgroundImageUrl}
            backgroundColor={tileBackgroundColor}
            tileFontStyle={tileFontStyle}
          />
        </div>
      </div>
    </BenefitsSection>
  );
};

export default TrainingBenefitsMain;
